'use client';

import { AUTH_PROVIDER, SESSION_ERROR, SESSION_STATUS } from '@/constants/auth';
import { PropsWithChildren, useEffect, useState } from 'react';
import { useSession, signIn } from 'next-auth/react';
import { usePathname } from 'next/navigation';
import { PRIVATE_TOP_LEVEL_SEGMENTS } from '@/configs/routes';
import { ROUTE_SEGMENT } from '@/constants/routeSegments';
import { Shell } from '../shell';
import { Private } from './Private';
import { PrivateStateProvider } from './PrivateStateProvider';
import { LoadingPage } from '@/components/utils';

function isPrivatePath(pathname: string): boolean {
  const segments = pathname.split('/').filter(Boolean);
  if (
    segments.length >= 1 &&
    PRIVATE_TOP_LEVEL_SEGMENTS.includes(segments[0]!)
  ) {
    return true;
  }
  // /{orgName}/registry/...
  return segments.length >= 2 && segments[1] === ROUTE_SEGMENT.REGISTRY;
}

/** Back off between automatic recovery attempts when sign-in doesn't redirect. */
const SESSION_RECOVERY_RETRY_MS = 30_000;
/** If navigation hasn't started shortly after a successful sign-in response, retry. */
const SESSION_RECOVERY_NAVIGATION_TIMEOUT_MS = 10_000;

export default function PrivateGate({ children }: PropsWithChildren) {
  const { data: session, status } = useSession();
  const pathname = usePathname();
  const onPrivatePath = isPrivatePath(pathname);

  useEffect(() => {
    if (status === SESSION_STATUS.UNAUTHENTICATED && onPrivatePath) {
      void signIn(AUTH_PROVIDER, {
        callbackUrl: pathname + window.location.search,
      });
    }
  }, [status, onPrivatePath, pathname]);

  // Recover from a failed/expired token refresh. When the `jwt()` callback
  // can't refresh the Entra `id_token` it sets `session.error` but keeps the
  // session `authenticated`, so the stale token keeps getting rejected (401)
  // downstream and screens like the header profile hang forever. Nothing else
  // consumes this signal, so force a re-auth here: if the Entra SSO session is
  // still alive this is a silent round-trip that mints a fresh token and
  // returns the user to where they were; otherwise they land on the sign-in
  // page. Uses `redirect: false` so we can detect a failed/blocked redirect
  // and schedule another attempt instead of giving up after the first call.
  const [recoveryRetryNonce, setRecoveryRetryNonce] = useState(0);
  useEffect(() => {
    const hasSessionError =
      status === SESSION_STATUS.AUTHENTICATED &&
      (session?.error === SESSION_ERROR.REFRESH_TOKEN_ERROR ||
        session?.error === SESSION_ERROR.NO_REFRESH_TOKEN);

    if (!hasSessionError) {
      return;
    }

    let cancelled = false;
    const callbackUrl = pathname + window.location.search;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    let cancelNavigationListener: (() => void) | undefined;

    const scheduleRetry = (delayMs: number) => {
      if (cancelled) {
        return;
      }
      retryTimer = setTimeout(() => {
        if (cancelled) {
          return;
        }
        setRecoveryRetryNonce((nonce) => nonce + 1);
      }, delayMs);
    };

    /** Retry only when assign() did not start a navigation (blocked/cancelled). */
    const scheduleRetryUnlessNavigating = (delayMs: number) => {
      if (cancelled) {
        return;
      }

      let navigationStarted = false;
      const onPageHide = () => {
        navigationStarted = true;
        if (retryTimer) {
          clearTimeout(retryTimer);
          retryTimer = undefined;
        }
      };

      window.addEventListener('pagehide', onPageHide, { once: true });
      cancelNavigationListener = () => {
        window.removeEventListener('pagehide', onPageHide);
      };

      retryTimer = setTimeout(() => {
        cancelNavigationListener?.();
        cancelNavigationListener = undefined;
        if (cancelled || navigationStarted) {
          return;
        }
        setRecoveryRetryNonce((nonce) => nonce + 1);
      }, delayMs);
    };

    void signIn(AUTH_PROVIDER, { callbackUrl, redirect: false })
      .then((result) => {
        if (cancelled) {
          return;
        }
        if (result?.ok && result.url) {
          window.location.assign(result.url);
          // assign() resolves even when navigation is blocked. pagehide fires
          // as soon as a redirect begins — wait for that before retrying so a
          // slow OAuth round-trip doesn't spawn overlapping sign-in flows.
          scheduleRetryUnlessNavigating(SESSION_RECOVERY_NAVIGATION_TIMEOUT_MS);
          return;
        }
        scheduleRetry(SESSION_RECOVERY_RETRY_MS);
      })
      .catch(() => {
        if (cancelled) {
          return;
        }
        scheduleRetry(SESSION_RECOVERY_RETRY_MS);
      });

    return () => {
      cancelled = true;
      cancelNavigationListener?.();
      cancelNavigationListener = undefined;
      if (retryTimer) {
        clearTimeout(retryTimer);
      }
    };
  }, [status, session?.error, pathname, recoveryRetryNonce]);

  if (
    (status === SESSION_STATUS.LOADING ||
      status === SESSION_STATUS.UNAUTHENTICATED) &&
    onPrivatePath
  ) {
    return <LoadingPage height="100vh" />;
  }

  // `PrivateStateProvider` owns the lifted `PrivateContext` state and wraps
  // `<Shell>` so the navbar's user-aware bits keep reading from the same
  // context instance regardless of auth status. `<Private>` mounts
  // unconditionally and self-gates its auth-dependent work on `useSession()`,
  // keeping the route subtree's tree position stable across the LOADING →
  // AUTHENTICATED transition so it does not remount (and lose SWR cache +
  // local state) when the session resolves.
  return (
    <PrivateStateProvider>
      <Shell>
        <Private>{children}</Private>
      </Shell>
    </PrivateStateProvider>
  );
}
